import { Button, Center, Collapse, Loader, TextInput } from "@mantine/core";
import React, { useMemo, useState } from "react";
import IconSearch from "../../../components/icons/icon-search";
import type { Permission, PermissionGroup } from "../types";

interface PermissionItemProps {
  permission: Permission;
  isChecked: boolean;
  onToggle: () => void;
}

const PermissionItem: React.FC<PermissionItemProps> = ({ permission, isChecked, onToggle }) => (
  <div className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-600 text-white">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
      </svg>
    </div>
    <div className="flex-1">
      <label htmlFor={`perm-${permission.id}`} className="cursor-pointer">
        <h4 className="font-semibold text-slate-800">{permission.name}</h4>
        <p className="text-sm text-slate-500">{permission.description}</p>
      </label>
    </div>
    <div className="pl-4">
      <input type="checkbox" id={`perm-${permission.id}`} checked={isChecked} onChange={onToggle} className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500" />
    </div>
  </div>
);

interface PermissionListPanelProps {
  permissions: Permission[];
  selectedItemName: string | null | undefined;
  stagedPermissions: Set<number>;
  isLoadingAll: boolean;
  isLoadingItem: boolean;
  isSaving: boolean;
  isDirty: boolean;
  onTogglePermission: (id: number) => void;
  onSave: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export const PermissionListPanel: React.FC<PermissionListPanelProps> = ({
  permissions, selectedItemName, stagedPermissions, isLoadingAll, isLoadingItem, isSaving, isDirty,
  onTogglePermission, onSave, searchTerm, onSearchChange
}) => {
  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, perm) => {
      const group = perm.group || 'Chưa phân loại';
      if (!acc[group]) acc[group] = [];
      acc[group].push(perm);
      return acc;
    }, {} as PermissionGroup);
  }, [permissions]);

  const [closedGroups, setClosedGroups] = useState(new Set<string>());

  const handleToggleGroupOpen = (groupName: string) => {
    setClosedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };
  
  const handleToggleGroup = (groupName: string, permsInGroup: Permission[]) => {
    const allChecked = permsInGroup.every(perm => stagedPermissions.has(perm.id));
    
    permsInGroup.forEach(perm => {
        const isCurrentlyChecked = stagedPermissions.has(perm.id);
        if (allChecked && isCurrentlyChecked) {
            onTogglePermission(perm.id); // Bỏ chọn
        } else if (!allChecked && !isCurrentlyChecked) {
            onTogglePermission(perm.id); // Chọn
        }
    });
  };


  const renderContent = () => {
    if (!selectedItemName) {
      return <Center className="h-full"><p className="text-slate-500">Vui lòng chọn một nhân viên để xem phân quyền</p></Center>;
    }
    if (isLoadingAll || isLoadingItem) {
      return <Center className="py-20"><Loader /></Center>;
    }
    if (Object.keys(groupedPermissions).length === 0) {
      return <Center className="py-20"><p className="text-slate-500">Không có dữ liệu quyền nào.</p></Center>;
    }
    return (
      <div className="space-y-4">
        {Object.entries(groupedPermissions).map(([groupName, permsInGroup]) => {
          const isOpen = !closedGroups.has(groupName);
          
          const checkedCount = permsInGroup.filter(perm => stagedPermissions.has(perm.id)).length;
          const isGroupChecked = checkedCount === permsInGroup.length;
          const isGroupIndeterminate = checkedCount > 0 && checkedCount < permsInGroup.length;
          
          return (
            <div key={groupName} className="rounded-lg border border-slate-200 bg-white">
                
              <div className="flex w-full items-center justify-between border-b border-slate-200 bg-slate-100 p-4">
                
                <button 
                    type="button" 
                    className="flex w-full items-center justify-between text-left focus:outline-none" 
                    onClick={() => handleToggleGroupOpen(groupName)}
                >
                    <span className="font-bold uppercase text-slate-700">{groupName}</span>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}>
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>

                <input 
                    type="checkbox" 
                    checked={isGroupChecked}
                    ref={el => el && (el.indeterminate = isGroupIndeterminate)}
                    onChange={() => handleToggleGroup(groupName, permsInGroup)}
                    className="h-5 w-5 flex-shrink-0 rounded border-gray-300 text-green-600 focus:ring-green-500 ml-4" 
                />
              </div>

              <Collapse in={isOpen}>
                <div className="space-y-4 p-4">
                  {permsInGroup.map((perm) => (
                    <PermissionItem key={perm.id} permission={perm} isChecked={stagedPermissions.has(perm.id)} onToggle={() => onTogglePermission(perm.id)} />
                  ))}
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-4 border-b bg-white p-4 shadow-sm">
        <h2 className="whitespace-nowrap text-sm font-bold uppercase tracking-widest text-green-800">
          Quyền của: <span className="text-green-600">{selectedItemName || "..."}</span>
        </h2>
        <div className="flex flex-1 justify-center px-8">
          <TextInput placeholder="Tìm kiếm quyền..." icon={<IconSearch />} value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} className="w-full max-w-md" />
        </div>
        <Button color="green" onClick={onSave} loading={isSaving} disabled={!isDirty || !selectedItemName}>
          Lưu Lại
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
        {renderContent()}
      </div>
    </div>
  );
};